import { useEffect, useId, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { BadgeCheck, Search } from "lucide-react";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@shared-kernel/primary/shared/shadcn/ui/form";
import { Input } from "@shared-kernel/primary/shared/shadcn/ui/input";
import { Popover, PopoverAnchor, PopoverContent } from "@shared-kernel/primary/shared/shadcn/ui/popover";
import { Skeleton } from "@shared-kernel/primary/shared/shadcn/ui/skeleton";
import { cn } from "@shared-kernel/primary/shared/shadcn/utils/utils";
import { SUPPORTED_COUNTRY } from "@shared-kernel/domain/types/supported-country";
import type { SiretSuggestion } from "@shared-kernel/application/ports/siret-search/siret-search-repository";
import { formatSiret } from "@utils/formatting";
import { useSiretSearch } from "./use-siret-search";

interface Props {
  siretFieldName: string;
  countryFieldName: string;
  nameFieldName?: string;
  label?: string;
  placeholder?: string;
}

export function SiretAutocompleteField({
  siretFieldName,
  countryFieldName,
  nameFieldName,
  label = "SIRET",
  placeholder = "SIRET, SIREN, nom ou adresse...",
}: Props) {
  const { control, watch, setValue } = useFormContext();
  const country = watch(countryFieldName) as SUPPORTED_COUNTRY | undefined;
  const isFrance = country === SUPPORTED_COUNTRY.FRANCE;
  const { query, setQuery, suggestions, isLoading } = useSiretSearch();
  const [open, setOpen] = useState(false);
  const [pickedFromRegistry, setPickedFromRegistry] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const triggerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [triggerWidth, setTriggerWidth] = useState<number>(0);
  const listboxId = useId();

  useEffect(() => {
    if (triggerRef.current) setTriggerWidth(triggerRef.current.offsetWidth);
  }, [open, isFrance]);

  useEffect(() => {
    setActiveIndex(0);
  }, [suggestions]);

  useEffect(() => {
    if (!listRef.current) return;
    const activeEl = listRef.current.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`);
    activeEl?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  return (
    <FormField
      control={control}
      name={siretFieldName}
      render={({ field }) => {
        if (!isFrance) {
          return (
            <FormItem>
              <FormLabel>{label}</FormLabel>
              <FormControl>
                <Input {...field} placeholder="14 chiffres" />
              </FormControl>
              <FormMessage />
            </FormItem>
          );
        }

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const value = e.target.value;
          field.onChange(value);
          setQuery(value);
          setPickedFromRegistry(false);
          setOpen(true);
        };

        const handleSelect = (suggestion: SiretSuggestion) => {
          setValue(siretFieldName, formatSiret(suggestion.siret), { shouldDirty: true, shouldValidate: true });
          if (nameFieldName) {
            setValue(nameFieldName, suggestion.name, { shouldDirty: true, shouldValidate: true });
          }
          setPickedFromRegistry(true);
          setQuery("");
          setOpen(false);
        };

        const fieldValue = typeof field.value === "string" ? field.value : "";
        const shouldOpenOnFocus = query.length >= 3 || fieldValue.length === 0;

        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key === "Escape") {
            if (open) {
              e.preventDefault();
              setOpen(false);
            }
            return;
          }
          if (e.key === "ArrowDown") {
            e.preventDefault();
            if (!open) {
              setOpen(true);
              return;
            }
            if (suggestions.length === 0) return;
            setActiveIndex(i => Math.min(i + 1, suggestions.length - 1));
            return;
          }
          if (e.key === "ArrowUp") {
            if (!open || suggestions.length === 0) return;
            e.preventDefault();
            setActiveIndex(i => Math.max(i - 1, 0));
            return;
          }
          if (e.key === "Enter") {
            if (!open || suggestions.length === 0) return;
            const target = suggestions[activeIndex];
            if (target) {
              e.preventDefault();
              handleSelect(target);
            }
          }
        };

        const showHint = open && query.length < 3 && !isLoading;
        const showEmpty = open && !isLoading && query.length >= 3 && suggestions.length === 0;
        const showResults = open && !isLoading && suggestions.length > 0;
        const showLoading = open && isLoading;

        return (
          <FormItem>
            <div className="flex items-center gap-2">
              <FormLabel>{label}</FormLabel>
              {pickedFromRegistry && (
                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-600">
                  <BadgeCheck className="size-3.5" />
                  Récupéré depuis le registre
                </span>
              )}
            </div>
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverAnchor asChild>
                <div ref={triggerRef}>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={placeholder}
                      autoComplete="off"
                      onChange={handleChange}
                      onBlur={() => {
                        // A pasted "123 456 789 00012" is accepted as is; the mapper sends it compact.
                        field.onChange(formatSiret(fieldValue));
                        field.onBlur();
                      }}
                      onKeyDown={handleKeyDown}
                      onFocus={() => {
                        if (shouldOpenOnFocus) setOpen(true);
                      }}
                      onClick={() => {
                        if (shouldOpenOnFocus) setOpen(true);
                      }}
                      role="combobox"
                      aria-expanded={open}
                      aria-controls={listboxId}
                      aria-autocomplete="list"
                      aria-activedescendant={open && showResults ? `${listboxId}-option-${activeIndex}` : undefined}
                    />
                  </FormControl>
                </div>
              </PopoverAnchor>
              <PopoverContent
                className="overflow-hidden p-0"
                align="start"
                sideOffset={4}
                style={{ width: triggerWidth || undefined }}
                onOpenAutoFocus={e => e.preventDefault()}
              >
                {showHint && (
                  <div className="flex items-center gap-2 p-3 text-sm text-muted-foreground">
                    <Search className="size-4 shrink-0" />
                    <span>Saisir au moins 3 caractères pour rechercher</span>
                  </div>
                )}
                {showLoading && (
                  <div className="space-y-2 p-2">
                    <Skeleton className="h-12 w-full" />
                    <Skeleton className="h-12 w-full" />
                    <Skeleton className="h-12 w-full" />
                  </div>
                )}
                {showEmpty && (
                  <div className="space-y-1 p-3 text-sm">
                    <p className="font-medium text-foreground">Aucun résultat</p>
                    <p className="text-xs text-muted-foreground">
                      Vous pouvez saisir le SIRET manuellement — utile pour les structures récentes absentes du registre.
                    </p>
                  </div>
                )}
                {showResults && (
                  <ul ref={listRef} id={listboxId} role="listbox" className="max-h-72 divide-y divide-border/60 overflow-auto">
                    {suggestions.map((suggestion, index) => {
                      const isActive = index === activeIndex;
                      return (
                        <li key={suggestion.siret} role="presentation">
                          <button
                            type="button"
                            id={`${listboxId}-option-${index}`}
                            role="option"
                            aria-selected={isActive}
                            data-index={index}
                            tabIndex={-1}
                            onClick={() => handleSelect(suggestion)}
                            onMouseEnter={() => setActiveIndex(index)}
                            className={cn(
                              "flex w-full flex-col items-start gap-0.5 px-3 py-2.5 text-left transition-colors",
                              "focus:outline-none",
                              isActive ? "bg-accent" : "hover:bg-accent/60",
                            )}
                          >
                            <span className="text-sm font-medium leading-tight text-foreground">{suggestion.name || suggestion.siret}</span>
                            {suggestion.address && <span className="text-xs leading-snug text-muted-foreground">{suggestion.address}</span>}
                            <span className="font-mono text-[11px] tracking-tight text-muted-foreground/70">
                              {formatSiret(suggestion.siret)}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </PopoverContent>
            </Popover>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
